// Phase 3.1a — Stocks table (presentation only).
//
// Extracted from the `inventoryManagement.tsx` monolith. This component ONLY
// restyles the antd `<Table>` into the shadcn `DataTable`. Every callback it
// receives (color/size change, row-action menu, checkbox selection, select-all)
// is dispatched by the parent EXACTLY as before — no state, payloads, saga or
// status/menu logic changed. The four legacy column variants (normal, download,
// instock, completed) and their per-status action menus are reproduced verbatim.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { MoreVertical, ShoppingCart, Store } from "lucide-react";
import AppConstant from "../../Globals/AppConstants";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Checkbox } from "ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui/tooltip";

// Mode mirrors the legacy `inventoryManagementTable()` column selection:
//   !completed && !download -> normal
//   download                -> download
//   instock                 -> instock
//   otherwise (completed)   -> completed
type Mode = "normal" | "download" | "instock" | "completed";

type MenuItem = { key: string; label: string; disabled?: boolean };

export interface InventoryStockTableProps {
  products: any[];
  loading?: boolean;
  sizeList?: any[];
  colorList?: any[];
  completedSticker?: boolean;
  downloadSticker?: boolean;
  instockSticker?: boolean;
  checkRecords?: any[];
  /** parent's addProductColorAndSizeApi(color, size, item) — unchanged. */
  onColorSize: (color: number, size: number, item: any) => void;
  /** parent's inventoryMenuProcess(key, item) — unchanged. */
  onMenuAction: (key: string, item: any) => void;
  /** parent's onChange(item, e) — receives a synthetic {target:{checked}} event. */
  onToggleRow?: (item: any, e: { target: { checked: boolean } }) => void;
  /** parent's selectAll(e) — receives a synthetic {target:{checked}} event. */
  onToggleAll?: (e: { target: { checked: boolean } }) => void;
  /** Rendered at the BOTTOM of the table (same spot as every other migrated
   * screen, e.g. Billing) instead of the toolbar's top-row prev/next controls. */
  pagination?: { page: number; total: number; onChange: (page: number) => void };
}

// --- Action menus: one builder per legacy `*Menu` function -------------------
// Attachment and Download Sticker are disabled (not deleted) per product
// request — commented out in every menu below so they're easy to re-enable.
const attach: MenuItem = { key: "10", label: "Attachment" };
function menu(item: any): MenuItem[] {
  return [
    { key: "1", label: AppConstant.edit },
    /* { key: "2", label: AppConstant.downloadSticker }, */
    { key: "3", label: AppConstant.completedSticker, disabled: item?.isStickered == 1 },
    { key: "4", label: AppConstant.damaged, disabled: item?.statusRefId == 5 },
    { key: "5", label: AppConstant.inStock, disabled: item?.statusRefId == 2 },
    { key: "7", label: AppConstant.history },
    /* attach, */
  ];
}
const noStatusMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  { key: "3", label: AppConstant.completedSticker },
  { key: "4", label: AppConstant.damaged },
  { key: "7", label: AppConstant.history },
  /* { key: "9", label: AppConstant.scrap }, */
  /* attach, */
];
const instockMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "4", label: AppConstant.damaged },
  { key: "7", label: AppConstant.history },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  /* { key: "9", label: AppConstant.scrap }, */
  /* attach, */
];
const damagedMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "5", label: AppConstant.inStock },
  { key: "7", label: AppConstant.history },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  /* { key: "9", label: AppConstant.scrap }, */
  /* attach, */
];
const returnedMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "5", label: AppConstant.inStock },
  { key: "4", label: AppConstant.damaged },
  { key: "7", label: AppConstant.history },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  /* { key: "9", label: AppConstant.scrap }, */
  /* attach, */
];
const soldMenu = (): MenuItem[] => [{ key: "7", label: AppConstant.history } /*, attach */];
const outOfStockMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "8", label: AppConstant.view },
  { key: "7", label: AppConstant.history },
  /* attach, */
];
const stickeringCompletedMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "5", label: AppConstant.inStock },
  { key: "4", label: AppConstant.damaged },
  { key: "7", label: AppConstant.history },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  /* { key: "9", label: AppConstant.scrap }, */
  /* attach, */
];
const scrapMenu = (): MenuItem[] => [/* { key: "7", label: AppConstant.history }, attach */];
const noMultiStatusMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  { key: "3", label: AppConstant.completedSticker },
  /* attach, */
];
const multiStickeringCompletedStatusMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "5", label: AppConstant.inStock },
  { key: "7", label: AppConstant.history },
  /* { key: "2", label: AppConstant.downloadSticker }, */
  /* attach, */
];
const multiInstockStatusMenu = (): MenuItem[] => [
  { key: "1", label: AppConstant.edit },
  { key: "8", label: AppConstant.view },
  { key: "7", label: AppConstant.history },
  // { key: "2", label: AppConstant.downloadSticker },
  /* attach, */
];
const multiSoldStatusMenu = (): MenuItem[] => [
  { key: "8", label: AppConstant.view },
  { key: "7", label: AppConstant.history },
  /*, attach */
];

// --- Per-mode action-menu selection (reproduces each variant's overlay={}) ---
function menuItemsFor(mode: Mode, item: any): MenuItem[] {
  const mr = item?.isMultipleRecords;
  const s = item?.statusRefId;
  if (mode === "instock") return stickeringCompletedMenu();
  if (mode === "download") {
    if (mr == 1 && s == null) return noStatusMenu();
    if (mr == 1 && s == 2) return instockMenu();
    if (mr == 1 && s == 5) return damagedMenu();
    if (mr == 1 && s == 4) return returnedMenu();
    if (mr == 1 && s == 9) return scrapMenu();
    if (mr == 1 && s == 3) return soldMenu();
    if (mr == 1 && s == 6) return stickeringCompletedMenu();
    return menu(item);
  }
  if (mode === "completed") {
    if (mr == 1 && s == null) return noStatusMenu();
    if (mr == 1 && s == 2) return instockMenu();
    if (mr == 1 && s == 5) return damagedMenu();
    if (mr == 1 && s == 4) return returnedMenu();
    if (mr == 1 && s == 3) return soldMenu();
    if (mr == 1 && s == 6) return stickeringCompletedMenu();
    return menu(item);
  }
  // normal
  if (mr == 1 && s == null) return noStatusMenu();
  if (mr == 1 && s == 2) return instockMenu();
  if (mr == 1 && s == 5) return damagedMenu();
  if (mr == 1 && s == 3) return soldMenu();
  if (mr == 1 && s == 4) return returnedMenu();
  if (mr == 1 && s == 6) return stickeringCompletedMenu();
  if (mr == 1 && s == 9) return scrapMenu();
  if (mr == 0 && s == null) return noMultiStatusMenu();
  if (mr == 0 && s == 6) return multiStickeringCompletedStatusMenu();
  /* if (mr == 0 && s == 10) return outOfStockMenu(); */
  if (s == 10) return outOfStockMenu();
  if (s == 3) return multiSoldStatusMenu();
  if (mr == 0 && s == 2) return multiInstockStatusMenu();
  return menu(item);
}

// --- Per-mode status text (reproduces each variant's status cell) ------------
function statusTextFor(mode: Mode, item: any): string {
  const mr = item?.isMultipleRecords;
  const s = item?.statusRefId;
  // if (mode === "normal") {
  //   if (mr == 1 && s > 0) return item?.status;
  //   if (mr == 0 && (s == 6 || s == 5 || s == 2 || s == 4 || s == 3)) return "Stickering Completed";
  //   if (mr == 0 && s == 10) return "Out of Stock";
  //   return "-";
  // }
  if (mode === "download") {
    if (mr == 1 && s == 5) return "Damaged";
    if (mr == 1 && s == 2) return "In Stock";
    if (mr == 1 && s == 4) return "Returned";
    if (mr == 1 && s == 3) return "Sold";
    if (mr == 1 && s == 6) return "Stickering Completed";
    if (mr == 0 && (s == 6 || s == 5 || s == 2 || s == 4 || s == 3)) return "Stickering Completed";
    if (mr == 0 && s == 10) return "Out of Stock";
    return "-";
  }
  // instock / completed
  return s > 0 ? item?.status : "-";
}

// --- Cell fragments ----------------------------------------------------------
function SalesTypeIcon({ item }: { item: any }) {
  const online = item?.salesType == AppConstant.two;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          {online ? (
            <ShoppingCart className="h-4 w-4" style={{ color: "#50C878" }} />
          ) : (
            <Store className="h-4 w-4" style={{ color: "#5E05C6" }} />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{online ? "Online" : "Offline"}</TooltipContent>
    </Tooltip>
  );
}

function ActionMenu({
  mode,
  item,
  onMenuAction,
  navigate,
}: {
  mode: Mode;
  item: any;
  onMenuAction: (key: string, item: any) => void;
  navigate: any;
}) {
  const items = menuItemsFor(mode, item);
  const handleMenuClick = (key: string, item: any) => {
    if (key === "7") {
      navigate(`/inventoryManagement/stock-history?productDetailId=${encodeURIComponent(item?.productDetailsId)}&productId=${encodeURIComponent(item?.productId ?? "")}`);
    } else {
      onMenuAction(key, item);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Row actions" onClick={(e) => e.stopPropagation()}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((it) => (
          <DropdownMenuItem
            key={it.key}
            disabled={it.disabled}
            onClick={() => handleMenuClick(it.key, item)}
          >
            {it.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={() => onMenuAction("11", item)}>Copy</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusCell({ mode, item }: { mode: Mode; item: any }) {
  const text = statusTextFor(mode, item);
  if (!text || text === "-") return <span className="text-muted-foreground">-</span>;
  return <StatusBadge status={text} />;
}

export function InventoryStockTable({
  products,
  loading,
  sizeList = [],
  colorList = [],
  completedSticker,
  downloadSticker,
  instockSticker,
  checkRecords = [],
  onColorSize,
  onMenuAction,
  onToggleRow,
  onToggleAll,
  pagination,
}: InventoryStockTableProps) {
  const navigate = useNavigate();
  const mode: Mode = !completedSticker && !downloadSticker ? "normal" : downloadSticker ? "download" : instockSticker ? "instock" : "completed";
  const isSticker = mode !== "normal";

  const allChecked = (products?.length ?? 0) > 0 && checkRecords?.length === products?.length;

  const columns = React.useMemo<ColumnDef<any, any>[]>(() => {
    const checkboxCol: ColumnDef<any, any> = {
      id: "select",
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={allChecked}
          onCheckedChange={(v) => onToggleAll?.({ target: { checked: v === true } })}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => {
        const item = row.original;
        const checked = !!checkRecords.find((x: any) => x?.productId == item?.productId);
        return (
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => onToggleRow?.(item, { target: { checked: v === true } })}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        );
      },
    };

    const idCol: ColumnDef<any, any> = {
      id: "productId",
      header: AppConstant.productId,
      enableSorting: false,
      meta: { sticky: "left" },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center gap-1.5">
            {!isSticker && (
              <Tooltip>
                {/* <TooltipTrigger asChild>
                  <span className="inline-flex text-muted-foreground"><Info className="h-3.5 w-3.5" /></span>
                </TooltipTrigger> */}
                <TooltipContent>{item?.purchaseDate ? moment(item.purchaseDate).format("DD-MMM-YYYY") : "-"}</TooltipContent>
              </Tooltip>
            )}
            <span className="max-w-[140px] truncate" title={item?.productId}>{item?.productId}</span>
          </div>
        );
      },
    };

    const nameCol: ColumnDef<any, any> = {
      id: "name",
      header: "Name",
      enableSorting: false,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center gap-2">
            <SalesTypeIcon item={item} />
            <span className="max-w-[140px] truncate" title={item?.productName}>{item?.productName}</span>
            {/* Combo stock (stockType 2) — built from other regular stock. */}
            {item?.stockType == 2 && <Badge variant="accent">Combo</Badge>}
          </div>
        );
      },
    };

    const mrpCol: ColumnDef<any, any> = { id: "mrp", header: "MRP", enableSorting: false, cell: ({ row }) => <span className="tabular-nums">₹ {row.original?.mrp}</span> };
    const statusCol: ColumnDef<any, any> = { id: "status", header: "Status", enableSorting: false, cell: ({ row }) => <StatusCell mode={mode} item={row.original} /> };
    const actionCol: ColumnDef<any, any> = { id: "action", header: "Action", enableSorting: false, meta: { sticky: "right" }, cell: ({ row }) => <ActionMenu mode={mode} item={row.original} onMenuAction={onMenuAction} navigate={navigate} /> };

    if (isSticker) {
      // download / instock / completed columns (with checkbox).
      return [
        checkboxCol,
        idCol,
        nameCol,
        { id: "category", header: "Category", enableSorting: false, cell: ({ row }) => <span className="max-w-[120px] truncate block" title={row.original?.categoryName}>{row.original?.categoryName}</span> },
        { id: "subCategory", header: "Sub-Category", enableSorting: false, cell: ({ row }) => <span className="max-w-[160px] truncate block" title={row.original?.subCategoryName}>{row.original?.subCategoryName}</span> },
        mrpCol,
        { id: "itemCount", header: "Item Count", enableSorting: false, cell: ({ row }) => <span className="tabular-nums">{row.original?.minimumQuantity}</span> },
        { id: "createdOn", header: "Created On", enableSorting: false, cell: ({ row }) => <span title={moment(row.original?.createdOn).format("DD-MMM-YYYY / hh:mm A")}>{moment(row.original?.createdOn).format("DD-MMM-YYYY / hh:mm A")}</span> },
        statusCol,
        actionCol,
      ];
    }

    // normal columns (no checkbox).
    return [
      idCol,
      nameCol,
      {
        id: "category",
        header: "Category",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="max-w-[180px]" title={`Core Category: ${row.original?.coreCategoryName}\nType & Sub Category: ${row.original?.categoryName} - ${row.original?.subCategoryName}`}>
            <span className="block truncate">{row.original?.coreCategoryName}</span>
            <span className="block truncate">{row.original?.categoryName} - {row.original?.subCategoryName}</span>
          </div>
        ),
      },
      mrpCol,
      { id: "available", header: AppConstant.available, enableSorting: false, cell: ({ row }) => <span className="tabular-nums">{row.original?.availableCount}</span> },
      { id: "sold", header: "Sold", enableSorting: false, cell: ({ row }) => <span className="tabular-nums">{row.original?.soldCount}</span> },
      statusCol,
      { id: "vendorName", header: "Vendor Name", enableSorting: false, cell: ({ row }) => <span className="max-w-[120px] truncate block" title={row.original?.vendorName}>{row.original?.vendorName ? row.original?.vendorName : "-"}</span> },
      {
        id: "purchaseDate",
        header: "Purchase Date",
        enableSorting: false,
        cell: ({ row }) => <span>{row.original?.purchaseDate ? moment(row.original.purchaseDate).format("DD-MMM-YYYY") : "-"}</span>,
      },
      { id: "createdBy", header: "Created By", enableSorting: false, cell: ({ row }) => <span className="max-w-[120px] truncate block" title={row.original?.createdBy}>{row.original?.createdBy ? row.original?.createdBy : "-"}</span> },
      {
        id: "createdOn",
        header: "Created On",
        cell: ({ row }) => (row.original?.createdOn ? moment(row.original?.createdOn).format("DD-MM-YY, hh:mm A") : "-"),
      },
      actionCol,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isSticker, checkRecords, allChecked]);

  return (
    <TooltipProvider delayDuration={200}>
      <DataTable columns={columns} data={products || []} loading={loading} pagination={pagination} />
    </TooltipProvider>
  );
}

export default InventoryStockTable;
